import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { IAiSpreadsheetSheet } from "@/payroll/ai-script/types";

const SHEET_TAB_SCROLL_RATIO = 0.8;
const SHEET_TAB_SCROLL_EDGE_TOLERANCE = 1;

interface UseSheetTabScrollParams {
  activeSheetId: string;
  sheets: IAiSpreadsheetSheet[];
}

export const useSheetTabScroll = ({
  activeSheetId,
  sheets,
}: UseSheetTabScrollParams) => {
  const activeTabRef = useRef<HTMLButtonElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabViewportRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [scrollbarHeight, setScrollbarHeight] = useState(0);

  const updateScrollButtonState = useCallback(() => {
    const viewport = tabViewportRef.current;
    if (!viewport) return;

    const maximumScrollLeft = Math.max(
      0,
      viewport.scrollWidth - viewport.clientWidth,
    );
    setCanScrollLeft(viewport.scrollLeft > SHEET_TAB_SCROLL_EDGE_TOLERANCE);
    setCanScrollRight(
      viewport.scrollLeft < maximumScrollLeft - SHEET_TAB_SCROLL_EDGE_TOLERANCE,
    );
    // Zero with overlay scrollbars; the bar's height where the OS always shows one.
    setScrollbarHeight(viewport.offsetHeight - viewport.clientHeight);
  }, []);

  useLayoutEffect(() => {
    const viewport = tabViewportRef.current;
    const tabList = tabListRef.current;
    if (!viewport) return;

    updateScrollButtonState();
    viewport.addEventListener("scroll", updateScrollButtonState, {
      passive: true,
    });

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateScrollButtonState);
    resizeObserver?.observe(viewport);
    if (tabList) {
      resizeObserver?.observe(tabList);
    }

    return () => {
      viewport.removeEventListener("scroll", updateScrollButtonState);
      resizeObserver?.disconnect();
    };
  }, [sheets, updateScrollButtonState]);

  // Keep the active tab in view when the sheet changes programmatically (e.g. a
  // "go to cell" that jumps to another sheet). Scroll the tab-strip viewport
  // directly instead of using scrollIntoView, which can also scroll the page to
  // reveal the strip at the bottom of a tall workbook.
  useEffect(() => {
    const activeTab = activeTabRef.current;
    const viewport = tabViewportRef.current;
    if (!activeTab || !viewport) return;

    const activeTabBounds = activeTab.getBoundingClientRect();
    const viewportBounds = viewport.getBoundingClientRect();
    if (activeTabBounds.left < viewportBounds.left) {
      viewport.scrollBy({ left: activeTabBounds.left - viewportBounds.left });
    } else if (activeTabBounds.right > viewportBounds.right) {
      viewport.scrollBy({ left: activeTabBounds.right - viewportBounds.right });
    }
  }, [activeSheetId, sheets]);

  const scrollTabs = (direction: -1 | 1) => {
    const viewport = tabViewportRef.current;
    if (!viewport) return;

    viewport.scrollBy({
      left: direction * viewport.clientWidth * SHEET_TAB_SCROLL_RATIO,
      behavior: "smooth",
    });
  };

  return {
    activeTabRef,
    canScrollLeft,
    canScrollRight,
    scrollbarHeight,
    scrollTabs,
    tabListRef,
    tabViewportRef,
  };
};
